import { useContext } from 'react'

import {
  Box, Button, ModalContext, SidebarModal, SortableList,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignQuote from '@hooks/useCampaignQuote'
import useCampaignQuoteSections from '@hooks/useCampaignQuoteSections'
import useCampaignQuoteSection from '@hooks/useCampaignQuoteSection'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useEditCampaignQuoteSectionSort from './hooks/useEditCampaignQuoteSectionSort'

import DraggableCampaignQuoteSection from './components/DraggableCampaignQuoteSection'

const MODAL_KEY = 'EditCampaignQuoteSectionSortModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateCampaignQuoteSectionSort: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditCampaignQuoteSectionSortModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateCampaignQuoteSectionSort } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaignQuote: initCampaignQuote } = modalPayload

  const { campaignQuote, updating } = useCampaignQuote(initCampaignQuote)

  const campaignQuoteSectionsPayload = useCampaignQuoteSections({
    filters: {
      campaign_quote_id: campaignQuote?.id,
    },
  })
  const {
    filteredCampaignQuoteSections,
  } = campaignQuoteSectionsPayload

  const sortPayload = useEditCampaignQuoteSectionSort({ filteredCampaignQuoteSections })
  const { callbacks: { modifyCampaignQuoteSectionSort }, sortedIds } = sortPayload

  const { callbacks: { updateQuoteSectionOrder } } = useCampaignQuoteSection({})

  // To be used with handleCallbackAction
  const updateQuoteSectionSortPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: updateQuoteSectionOrder,
      afterAction: closeModal,
    },
    entityParams: {
      campaignQuoteId: campaignQuote.id,
      sortedQuoteSectionIds: sortedIds,
    },
    toastText: 'Quote Sections Sorted',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Sort" titleSecondLine="Quote Sections" />

      <SidebarModal.Content>
        <Box flexDirection="column">
          <SortableList
            callbacks={{
              // @ts-expect-error TS2345
              onDragEnd: (fromIndex, toIndex, payload) => modifyCampaignQuoteSectionSort(fromIndex, toIndex, payload),
            }}
          >
            <SortableList.Dropzone droppableId="sortedIds">
              <Box flexDirection="column">
                {sortedIds.map((id, index) => (
                  <SortableList.Item
                    draggableId={`CampaignQuoteSection-${id}`}
                    key={id}
                    index={index}
                  >
                    {/*
                     // @ts-expect-error TS2322 */}
                    <DraggableCampaignQuoteSection campaignQuoteSectionId={id} />
                  </SortableList.Item>
                ))}
              </Box>
            </SortableList.Dropzone>
          </SortableList>
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateCampaignQuoteSectionSort(updateQuoteSectionSortPayload)}
          size="large"
        >
          Save Sort
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditCampaignQuoteSectionSortModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
